/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessResolveChallenge } from "../funcs/accessResolveChallenge.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ResolveChallengesResult } from "../models/components/resolvechallengesresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ResolveChallengeRequest,
  ResolveChallengeSecurity,
} from "../models/operations/resolvechallenge.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ResolveChallengeMutationVariables = {
  request: ResolveChallengeRequest;
  security?: ResolveChallengeSecurity | undefined;
  options?: RequestOptions;
};

export type ResolveChallengeMutationData = ResolveChallengesResult;

export type ResolveChallengeMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * resolveChallenge access
 *
 * @remarks
 * Dismiss one or more denied authz challenges, or atomically add one custom role to the denied user before recording the challenges as resolved.
 */
export function useResolveChallengeMutation(
  options?: MutationHookOptions<
    ResolveChallengeMutationData,
    ResolveChallengeMutationError,
    ResolveChallengeMutationVariables
  >,
): UseMutationResult<
  ResolveChallengeMutationData,
  ResolveChallengeMutationError,
  ResolveChallengeMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildResolveChallengeMutation(client, options),
    ...options,
  });
}

export function mutationKeyResolveChallenge(): MutationKey {
  return ["@gram/client", "access", "resolveChallenge"];
}

export function buildResolveChallengeMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ResolveChallengeMutationVariables,
  ) => Promise<ResolveChallengeMutationData>;
} {
  return {
    mutationKey: mutationKeyResolveChallenge(),
    mutationFn: function resolveChallengeMutationFn({
      request,
      security,
      options,
    }): Promise<ResolveChallengeMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessResolveChallenge(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
